import { swapCartDrawerFromHtml } from '@theme/na-cart-api';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {HTMLElement & { open?: () => void, showDialog?: () => void }} CartDrawerHost
 */

const CART_UPDATE_EVENT = 'cart:update';
const DRAWER_SELECTOR = 'cart-drawer-component';
const LOADER_SELECTOR = 'script[data-cart-stack-loader]';

/** @type {Promise<void> | null} */
let loadPromise = null;

/** @type {boolean} */
let pendingOpen = false;

/**
 * @returns {HTMLScriptElement | null}
 */
function getLoaderScript() {
    return document.querySelector(LOADER_SELECTOR);
}

/**
 * @returns {CartDrawerHost | null}
 */
function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
}

/**
 * @param {string} url
 * @returns {Promise<void>}
 */
function loadModuleScript(url) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = url;
        script.addEventListener('load', () => resolve(), { once: true });
        script.addEventListener('error', () => reject(new Error(`Failed to load ${url}`)), { once: true });
        document.head.appendChild(script);
    });
}

/**
 * @returns {string[]}
 */
function getModuleUrls() {
    const script = getLoaderScript();
    const raw = script?.dataset.modules;
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((url) => typeof url === 'string') : [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

/**
 * @param {boolean} isBusy
 */
function setBusy(isBusy) {
    const drawer = getDrawer();
    const region = drawer?.querySelector('[data-cart-stack-loading]');
    if (!drawer) return;

    if (isBusy) {
        drawer.setAttribute('aria-busy', 'true');
        region?.setAttribute('aria-busy', 'true');
        return;
    }

    drawer.removeAttribute('aria-busy');
    region?.removeAttribute('aria-busy');
}

/**
 * @param {CartDrawerHost} drawer
 */
function clearStubs(drawer) {
    if (Object.prototype.hasOwnProperty.call(drawer, 'open')) delete drawer.open;
    if (Object.prototype.hasOwnProperty.call(drawer, 'showDialog')) delete drawer.showDialog;
}

/**
 * @param {CartDrawerHost} drawer
 */
function attachStubs(drawer) {
    if (customElements.get('cart-drawer-component')) return;

    const open = () => {
        ensureLoaded({ open: true });
    };

    drawer.open = open;
    drawer.showDialog = open;
}

function loadCartStack() {
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        const urls = getModuleUrls();
        await Promise.all(urls.map((url) => loadModuleScript(url)));
        await customElements.whenDefined('cart-drawer-component');
        const drawer = getDrawer();
        if (drawer) clearStubs(drawer);
    })();

    return loadPromise;
}

/**
 * @param {Record<string, string> | undefined} sections
 */
async function applyCartSections(sections) {
    const cartItems = document.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
    if (!sectionId) return;

    const html = sections?.[sectionId];
    if (html) {
        swapCartDrawerFromHtml(html);
        return;
    }

    await sectionRenderer.renderSection(sectionId, { cache: false });
}

/**
 * @param {{ open?: boolean, sections?: Record<string, string>, refresh?: boolean, swapped?: boolean }} [options]
 */
async function ensureLoaded(options = {}) {
    if (options.open) pendingOpen = true;
    if (pendingOpen) setBusy(true);

    const stackWasReady = Boolean(customElements.get('cart-drawer-component'));
    const needsSectionApply = Boolean(options.sections) || Boolean(options.refresh);

    try {
        if (
            needsSectionApply &&
            !options.swapped &&
            (!stackWasReady || document.querySelector('.cart-drawer__content--empty'))
        ) {
            await applyCartSections(options.sections);
        }

        await loadCartStack();
        if (!pendingOpen) return;

        pendingOpen = false;
        const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (getDrawer());
        drawer?.open?.();
    } catch (error) {
        console.error(error);
    } finally {
        setBusy(false);
    }
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isCartOpenClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    const trigger = target.closest('[on\\:click]');
    if (!trigger) return false;

    return trigger.getAttribute('on:click') === '#na-cart-drawer/open';
}

const drawer = getDrawer();
if (drawer) attachStubs(drawer);

document.addEventListener(
    'click',
    (event) => {
        if (customElements.get('cart-drawer-component')) return;
        if (!isCartOpenClick(event)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        ensureLoaded({ open: true });
    },
    true
);

document.addEventListener(CART_UPDATE_EVENT, (event) => {
    const detail =
        /** @type {CustomEvent<{ data?: { didError?: boolean, sections?: Record<string, string>, swapped?: boolean } }>} */ (
            event
        ).detail;
    const shouldOpen = Boolean(getDrawer()?.hasAttribute('auto-open') && !detail?.data?.didError);
    ensureLoaded({
        open: shouldOpen,
        sections: detail?.data?.sections,
        refresh: !detail?.data?.didError,
        swapped: detail?.data?.swapped,
    });
});

const script = getLoaderScript();
if (script?.dataset.eager === 'true') {
    loadCartStack().catch((error) => console.error(error));
} else if (sessionStorage.getItem('openCartDrawer') === 'true') {
    ensureLoaded({ open: true });
} else {
    requestIdleCallback(() => {
        for (const url of getModuleUrls()) {
            const link = document.createElement('link');
            link.rel = 'modulepreload';
            link.href = url;
            document.head.appendChild(link);
        }
    });
}
